import { useEffect, useState } from 'react';
import { Download, ExternalLink } from 'lucide-react';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Skeleton, toast } from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { fetchAttachmentUrl } from '../hooks/useBusinessInquiries';
import { formatFileSize } from '../lib/businessInquiryLabels';
import type { BusinessInquiryAttachment } from '../types/businessInquiry';

interface Props {
  inquiryNo: number;
  attachment: BusinessInquiryAttachment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 사업자등록증 미리보기 팝업 — PDF 는 iframe, PNG·JPG 는 이미지로 띄운다(BE 가 이 세 형식만 받는다).
 * 비공개 파일이라 열 때마다 5분짜리 inline 주소를 새로 받는다. 다운로드는 attachment 주소를 따로 받아 내려받는다.
 */
export const AttachmentPreviewDialog = ({ inquiryNo, attachment, open, onOpenChange }: Props) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setUrl(null);
    setError(null);
    fetchAttachmentUrl(inquiryNo, 'inline')
      .then((u) => !cancelled && setUrl(u))
      .catch((e: unknown) => !cancelled && setError(friendlyMessage(e, '사업자등록증을 열지 못했습니다.')));
    return () => {
      cancelled = true;
    };
  }, [open, inquiryNo]);

  const onDownload = async () => {
    setDownloading(true);
    try {
      window.location.assign(await fetchAttachmentUrl(inquiryNo, 'attachment'));
    } catch (e) {
      toast.error(friendlyMessage(e, '사업자등록증을 내려받지 못했습니다.'));
    } finally {
      setDownloading(false);
    }
  };

  const isPdf = attachment.contentType === 'application/pdf';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-4xl flex-col gap-4" data-testid="business-inquiry-attachment-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-baseline gap-2 pr-6">
            <span className="break-all">{attachment.fileName}</span>
            <span className="text-[12px] font-normal" style={{ color: 'var(--content-muted)' }}>
              {formatFileSize(attachment.size)}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div
          className="flex h-[65vh] items-center justify-center overflow-auto rounded-md border"
          style={{ borderColor: 'var(--divider-default)', background: 'var(--surface-1)' }}
        >
          {error ? (
            <p className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
              {error}
            </p>
          ) : !url ? (
            <Skeleton className="h-full w-full" />
          ) : isPdf ? (
            <iframe src={url} title={attachment.fileName} className="h-full w-full" />
          ) : (
            <img src={url} alt={attachment.fileName} className="max-h-full max-w-full object-contain" />
          )}
        </div>

        <DialogFooter>
          {url ? (
            <Button asChild variant="outline">
              <a href={url} target="_blank" rel="noopener noreferrer" data-testid="business-inquiry-attachment-newtab">
                <ExternalLink size={14} aria-hidden="true" />새 창으로 열기
              </a>
            </Button>
          ) : null}
          <Button
            type="button"
            onClick={() => void onDownload()}
            disabled={downloading}
            data-testid="business-inquiry-attachment-dialog-download"
          >
            <Download size={14} aria-hidden="true" />
            다운로드
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
